import {
  Box,
  Card,
  Divider,
  GKEditor,
  GKTextField,
  Grid2,
  IconButton,
  LeftIcon,
  RightIcon,
  Show,
  Typography,
  UploadIcon,
} from "components";
import GKImageUploader from "components/GKImageUploader";
import { isEmpty } from "lodash";
import { useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useUpdateMyWebsiteDetailsMutation } from "store/features/MyWebsite";
import {
  setCroppedAdvisorImage,
  setFocusedField,
  setSelectedAdvisorImage,
} from "store/slices/MyWebsiteSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { validateNotWhitespaceOnly } from "utils/validationSchema";
import SymbolsModal from "./SymbolsModal";
import { WebsiteDetails } from "./types";

const HeroSectionForm = () => {
  const form = useFormContext<WebsiteDetails>();

  const dispatch = useAppDispatch();

  const id = useAppSelector((state) => state?.user?.userData?.companyId);
  const { croppedAdvisorImage, selectedAdvisorImage } = useAppSelector(
    (state) => state.myWebsite,
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [symbolModal, setSymbolModal] = useState(false);

  const [updateMyWebsiteDetails] = useUpdateMyWebsiteDetailsMutation();

  const handleScroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -150 : 150,
        behavior: "smooth",
      });
    }
  };

  const handleOpenSymbolModal = () => setSymbolModal(true);

  const handleFocus = (name: string) => {
    dispatch(setFocusedField(name));
  };

  const handleSetSelectedImage = (img: string | null | undefined) => {
    dispatch(setSelectedAdvisorImage(img));
  };

  const handleSetCroppedImage = (img: string | null | undefined) => {
    dispatch(setCroppedAdvisorImage(img));
  };

  const handleDeleteImage = async () => {
    if (isEmpty(form.getValues("websiteSection.heroSection.advisorPhoto"))) {
      dispatch(setSelectedAdvisorImage(null));
      dispatch(setCroppedAdvisorImage(null));
      return;
    }

    try {
      await updateMyWebsiteDetails({
        id,
        body: new FormData(),
        path: "hero_section",
        isDelete: true,
        field: "advisorPhoto",
      }).unwrap();
    } catch (err) {
      console.error("Update my website details failed:", err);
    }
  };

  return (
    <Grid2 container spacing={3}>
      <Grid2 container size={12} spacing={"12px"}>
        <Grid2
          size={12}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography fontSize={13} fontWeight={500} color="var(--grey-900)">
            Select symbol of wealth
          </Typography>
          <Typography
            fontSize={13}
            fontWeight={500}
            color="var(--primary-900)"
            onClick={handleOpenSymbolModal}
            display={"flex"}
            alignItems={"center"}
            gap={0.5}
            sx={{ cursor: "pointer" }}
          >
            View All
            <RightIcon />
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="websiteSection.heroSection.heroSectionLogo"
            control={form.control}
            render={({ field }) => (
              <Box
                ref={scrollRef}
                sx={{
                  display: "flex",
                  gap: 1,
                  overflowX: "auto",
                  scrollBehavior: "smooth",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {Array.from({ length: 17 }).map((item, idx) => (
                  <Card
                    key={idx}
                    sx={{
                      flex: "0 0 auto",
                      borderRadius: "12px",
                      p: 2,
                      border:
                        field.value !== null && +field.value === +idx
                          ? "3px solid var(--primary-900)"
                          : "1px solid var(--neutral-100)",
                      bgcolor: "var(--white)",
                      width: "80px",
                      height: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 28,
                      cursor: "pointer",
                    }}
                    onClick={() => field.onChange(idx)}
                  >
                    <img
                      src={`/RAWebsite/symbol-${idx}.png`}
                      alt=""
                      height={64}
                      width={64}
                    />
                  </Card>
                ))}
              </Box>
            )}
          />
        </Grid2>
        <Grid2
          size={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={"10px"}
        >
          <IconButton onClick={() => handleScroll("left")}>
            <LeftIcon />
          </IconButton>
          <IconButton onClick={() => handleScroll("right")}>
            <RightIcon />
          </IconButton>
        </Grid2>
      </Grid2>
      <Grid2 size={12}>
        <Divider />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.heroSection.companyMetaName"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter company name",
            },
            validate: validateNotWhitespaceOnly,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <GKEditor
                {...field}
                inputLabel="Firm Name"
                requiredField
                error={!!error}
                helperText={error?.message}
                minHeight={50}
                toolbar={[
                  "undo",
                  "redo",
                  "|",
                  "fontColor",
                  "fontBackgroundColor",
                ]}
                onFocus={() => handleFocus(field.name)}
                onBlur={() => handleFocus(null)}
              />
            );
          }}
        />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.heroSection.tagLine"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter tag line",
            },
            validate: validateNotWhitespaceOnly,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <GKEditor
                {...field}
                inputLabel="Title"
                requiredField
                error={!!error}
                helperText={error?.message}
                minHeight={50}
                toolbar={[
                  "undo",
                  "redo",
                  "|",
                  "bold",
                  "italic",
                  "underline",
                  "strikethrough",
                  "|",
                  "fontColor",
                  "fontBackgroundColor",
                ]}
                onFocus={() => handleFocus(field.name)}
                onBlur={() => handleFocus(null)}
              />
            );
          }}
        />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.heroSection.tagLineBody"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter tag line description",
            },
            validate: validateNotWhitespaceOnly,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <GKEditor
                {...field}
                inputLabel="Sub-title"
                requiredField
                error={!!error}
                helperText={error?.message}
                tooltipTitle={`Add a brief description that supports your tagline—highlight your strengths, philosophy, or what sets your firm apart.`}
                toolbar={[
                  "undo",
                  "redo",
                  "|",
                  "bold",
                  "italic",
                  "underline",
                  "strikethrough",
                  "|",
                  "fontColor",
                  "fontBackgroundColor",
                ]}
                onFocus={() => handleFocus(field.name)}
                onBlur={() => handleFocus(null)}
              />
            );
          }}
        />
      </Grid2>
      <Grid2 size={12} container spacing={1.2}>
        <Grid2 size={12}>
          <Typography fontSize={13} fontWeight={500} color="var(--grey-900)">
            Upload your Image
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="websiteSection.heroSection.advisorPhoto"
            control={form.control}
            render={() => (
              <GKImageUploader
                croppedImage={croppedAdvisorImage}
                handleProfilePicDelete={handleDeleteImage}
                selectedImage={selectedAdvisorImage}
                setCroppedImage={handleSetCroppedImage}
                setSelectedImage={handleSetSelectedImage}
                form={form}
                logoName={"websiteSection.heroSection.advisorPhoto"}
                buttonName="Upload Image"
                startIcon={<UploadIcon />}
                btnVariant={"outlined"}
              />
            )}
          />
        </Grid2>
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.heroSection.advisorName"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter advisor name",
            },
            validate: validateNotWhitespaceOnly,
          }}
          render={({ field, fieldState: { error } }) => (
            <GKTextField
              {...field}
              inputLabel="Your Full Name"
              requiredField
              error={!!error}
              helperText={error?.message}
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>

      <Show.When isTrue={!!symbolModal}>
        <SymbolsModal open={symbolModal} setOpen={setSymbolModal} />
      </Show.When>
    </Grid2>
  );
};

export default HeroSectionForm;
